import { db } from '../firebase';
import { collection, query, getDocs, writeBatch, doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';

/**
 * Migration utility to transition from the current tracking system to the optimized one
 * This preserves all historical data while reducing document count and eliminating redundancy
 */

// Constants
const BATCH_SIZE = 50; // Number of usages to batch together

/**
 * Migrate credit usage records to the new batched format
 * @returns {Promise<Object>} Migration statistics
 */
export const migrateCreditUsageRecords = async () => {
  try {
    console.log('Starting credit usage migration...');
    
    // Get all existing credit usage records
    const usageQuery = query(collection(db, 'credit_usage'));
    const usageSnapshot = await getDocs(usageQuery);
    
    if (usageSnapshot.empty) {
      console.log('No credit usage records to migrate');
      return { success: true, migrated: 0, batches: 0 };
    }
    
    // Group records by user and month
    const recordsByUserMonth = {};
    
    usageSnapshot.forEach(doc => {
      const usage = doc.data();
      const userId = usage.userId;
      
      if (!userId) {
        console.warn('Skipping record without userId:', doc.id);
        return;
      }
      
      // Get timestamp
      const timestamp = usage.timestamp?.toDate() || new Date();
      const year = timestamp.getFullYear();
      const month = timestamp.getMonth() + 1;
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
      const userMonthKey = `${userId}_${monthKey}`;
      
      // Initialize group if needed
      if (!recordsByUserMonth[userMonthKey]) {
        recordsByUserMonth[userMonthKey] = {
          userId,
          monthKey,
          usages: []
        };
      }
      
      // Determine usage type
      let usageType = 'other';
      if (usage.type === 'sizechart_generation' || usage.type === 'sizechart_comparison' || usage.usageType === 'sizechart') {
        usageType = 'sizechart';
      } else if (usage.templateId || usage.usageType === 'template') {
        usageType = 'template';
      } else if (usage.type === 'image_generation' || usage.type === 'image_processing' || usage.usageType === 'image') {
        usageType = 'image';
      }
      
      // Add usage record to group
      recordsByUserMonth[userMonthKey].usages.push({
        timestamp: usage.timestamp || Timestamp.now(),
        credits: usage.credits || 1,
        usageType,
        shopId: usage.shopId || null,
        templateId: usage.templateId || null,
        modelId: usage.modelId || null,
        productId: usage.productId || null,
        type: usage.type || null
      });
    });
    
    // Create batch documents for each group
    let batch = writeBatch(db);
    let batchCount = 0;
    let totalRecords = 0;
    let totalBatches = 0;
    
    for (const [userMonthKey, group] of Object.entries(recordsByUserMonth)) {
      // Split into batches of BATCH_SIZE
      const { userId, monthKey, usages } = group;
      
      for (let i = 0; i < usages.length; i += BATCH_SIZE) {
        const batchUsages = usages.slice(i, i + BATCH_SIZE);
        const batchId = i === 0 ? userMonthKey : `${userMonthKey}_${i}`;
        const batchRef = doc(db, 'credit_usage_batches', batchId);
        
        batch.set(batchRef, {
          userId,
          monthKey,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          usages: batchUsages
        });
        
        totalRecords += batchUsages.length;
        totalBatches++;
        batchCount++;
        
        // Commit batch if it's getting large (Firestore limit is 500 operations per batch)
        if (batchCount >= 400) {
          await batch.commit();
          console.log(`Committed batch with ${batchCount} documents (${totalRecords} records)`);
          batchCount = 0;
          batch = writeBatch(db);
        }
      }
    }
    
    // Commit any remaining operations
    if (batchCount > 0) {
      await batch.commit();
      console.log(`Committed final batch with ${batchCount} documents (${totalRecords} records)`);
    }
    
    console.log(`Credit usage migration complete: ${totalRecords} records migrated to ${totalBatches} batch documents`);
    return { success: true, migrated: totalRecords, batches: totalBatches };
  } catch (error) {
    console.error('Error migrating credit usage records:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate aggregated credit usage statistics from the batched records
 * @returns {Promise<Object>} Aggregation statistics
 */
export const generateCreditUsageStats = async () => {
  try {
    console.log('Starting credit usage stats generation...');
    
    // Initialize batch variables
    let batch;
    let batchCount = 0;
    
    // Get all batch documents
    const batchQuery = query(collection(db, 'credit_usage_batches'));
    const batchSnapshot = await getDocs(batchQuery);
    
    if (batchSnapshot.empty) {
      console.log('No credit usage batches found');
      return { success: true, users: 0 };
    }
    
    // Group by user
    const statsByUser = {};
    
    // Process all batches
    batchSnapshot.forEach(batchDoc => {
      const batch = batchDoc.data();
      const userId = batch.userId;
      
      if (!userId) {
        console.warn('Skipping batch without userId:', batchDoc.id);
        return;
      }
      
      // Initialize user stats if needed
      if (!statsByUser[userId]) {
        statsByUser[userId] = {
          total: 0,
          byType: {
            template: { total: 0, byDay: {}, byMonth: {} },
            image: { total: 0, byDay: {}, byMonth: {} },
            sizechart: { total: 0, byDay: {}, byMonth: {} },
            other: { total: 0, byDay: {}, byMonth: {} }
          },
          byDay: {},
          byMonth: {},
          byShop: {}
        };
      }
      
      // Process usages in this batch
      const usages = batch.usages || [];
      
      usages.forEach(usage => {
        const credits = usage.credits || 1;
        const usageType = usage.usageType || 'other';
        const shopId = usage.shopId;
        
        // Get date for daily and monthly aggregation
        let timestamp;
        try {
          timestamp = usage.timestamp.toDate();
        } catch (e) {
          timestamp = new Date(); // Fallback
        }
        
        const year = timestamp.getFullYear();
        const month = timestamp.getMonth() + 1;
        const day = timestamp.getDate();
        const dateKey = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
        
        // Update total
        statsByUser[userId].total += credits;
        
        // Update by type
        if (!statsByUser[userId].byType[usageType]) {
          statsByUser[userId].byType[usageType] = { total: 0, byDay: {}, byMonth: {} };
        }
        statsByUser[userId].byType[usageType].total += credits;
        
        // Update by day
        if (!statsByUser[userId].byDay[dateKey]) {
          statsByUser[userId].byDay[dateKey] = 0;
        }
        statsByUser[userId].byDay[dateKey] += credits;
        
        // Update by month
        if (!statsByUser[userId].byMonth[monthKey]) {
          statsByUser[userId].byMonth[monthKey] = 0;
        }
        statsByUser[userId].byMonth[monthKey] += credits;
        
        // Update by type and day
        if (!statsByUser[userId].byType[usageType].byDay[dateKey]) {
          statsByUser[userId].byType[usageType].byDay[dateKey] = 0;
        }
        statsByUser[userId].byType[usageType].byDay[dateKey] += credits;
        
        // Update by type and month
        if (!statsByUser[userId].byType[usageType].byMonth[monthKey]) {
          statsByUser[userId].byType[usageType].byMonth[monthKey] = 0;
        }
        statsByUser[userId].byType[usageType].byMonth[monthKey] += credits;
        
        // Update by shop if applicable
        if (shopId) {
          if (!statsByUser[userId].byShop[shopId]) {
            statsByUser[userId].byShop[shopId] = {
              total: 0,
              byType: {},
              byDay: {},
              byMonth: {}
            };
          }
          
          // Update shop total
          statsByUser[userId].byShop[shopId].total += credits;
          
          // Update shop by type
          if (!statsByUser[userId].byShop[shopId].byType[usageType]) {
            statsByUser[userId].byShop[shopId].byType[usageType] = { total: 0, byDay: {}, byMonth: {} };
          }
          statsByUser[userId].byShop[shopId].byType[usageType].total += credits;
          
          // Update shop by day
          if (!statsByUser[userId].byShop[shopId].byDay[dateKey]) {
            statsByUser[userId].byShop[shopId].byDay[dateKey] = 0;
          }
          statsByUser[userId].byShop[shopId].byDay[dateKey] += credits;
          
          // Update shop by month
          if (!statsByUser[userId].byShop[shopId].byMonth[monthKey]) {
            statsByUser[userId].byShop[shopId].byMonth[monthKey] = 0;
          }
          statsByUser[userId].byShop[shopId].byMonth[monthKey] += credits;
        }
      });
    });
    
    // Save aggregated stats to Firestore
    batch = writeBatch(db);
    batchCount = 0;
    
    for (const [userId, stats] of Object.entries(statsByUser)) {
      const statsRef = doc(db, 'credit_usage_stats', userId);
      
      batch.set(statsRef, {
        ...stats,
        lastUpdated: serverTimestamp()
      });
      
      batchCount++;
      
      // Commit batch if it's getting large
      if (batchCount >= 400) {
        await batch.commit();
        console.log(`Committed batch with ${batchCount} user stats`);
        batchCount = 0;
        batch = writeBatch(db);
      }
    }
    
    // Commit any remaining operations
    if (batchCount > 0) {
      await batch.commit();
      console.log(`Committed final batch with ${batchCount} user stats`);
    }
    
    console.log(`Credit usage stats generation complete: ${Object.keys(statsByUser).length} users processed`);
    return { success: true, users: Object.keys(statsByUser).length };
  } catch (error) {
    console.error('Error generating credit usage stats:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Run the complete migration process
 * @returns {Promise<Object>} Migration results
 */
export const runCompleteMigration = async () => {
  try {
    console.log('Starting complete tracking system migration...');
    
    // Step 1: Migrate credit usage records to batched format
    const creditMigrationResult = await migrateCreditUsageRecords();
    
    if (!creditMigrationResult.success) {
      throw new Error(`Credit migration failed: ${creditMigrationResult.error}`);
    }
    
    // Step 2: Generate aggregated credit usage stats
    const statsGenerationResult = await generateCreditUsageStats();
    
    if (!statsGenerationResult.success) {
      throw new Error(`Stats generation failed: ${statsGenerationResult.error}`);
    }
    
    // Step 3: Consolidate shop metrics (optional)
    const { consolidateShopMetrics } = await import('./optimizedShopMetricsService');
    const shopMetricsResult = await consolidateShopMetrics();
    
    console.log('Migration complete!');
    return {
      success: true,
      creditMigration: creditMigrationResult,
      statsGeneration: statsGenerationResult,
      shopMetricsConsolidation: shopMetricsResult
    };
  } catch (error) {
    console.error('Error during complete migration:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a migration admin page component
 * This can be imported into an admin page to run the migration
 */
export const MigrationAdminComponent = {
  name: 'MigrationAdmin',
  description: 'Run the tracking system migration to optimize Firestore usage',
  instructions: [
    'This migration will:',
    '1. Consolidate credit usage records into batched documents',
    '2. Generate aggregated credit usage statistics',
    '3. Optimize shop metrics to reduce redundancy',
    '',
    'The migration preserves all historical data while reducing document count.',
    'This is a one-time operation that should be run during off-peak hours.',
    '',
    'IMPORTANT: Back up your Firestore data before running this migration!'
  ]
};
